import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { web } from '@klever/sdk-web';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../redux/Store';
import { logout } from '../redux/slices/authSlice';

interface KleverWalletContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  address: string | null;
  balance: number | null;

  // Wallet actions
  connectWallet: () => Promise<string>; // Returns wallet address
  disconnectWallet: () => void;
  signMessage: (message: string) => Promise<string>;

  // ITO Actions
  buyITOTokens: (amountKLYX: number, pricePerToken: number, receiverAddress: string) => Promise<string>;

  // FPR Staking Actions
  freezeTokens: (amount: number, kdaId: string) => Promise<string>;
  unfreezeTokens: (bucketId: string, kdaId: string) => Promise<string>;
  claimStakingRewards: (claimType: number, kdaId: string) => Promise<string>;

  // Fee Pool Actions (if needed beyond regular transfers)
  depositToFeePool: (amount: number, poolAddress: string, kdaId: string) => Promise<string>;

  // Status
  error: string | null;
  isExtensionInstalled: boolean;
}

const KleverWalletContext = createContext<KleverWalletContextType | undefined>(undefined);

interface KleverWalletProviderProps {
  children: ReactNode;
}

export const KleverWalletProvider: React.FC<KleverWalletProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state) => state.auth.user);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);

  // Force re-login if the user switches accounts inside the Klever wallet
  // mid-session. Initialises the SDK once per session, then polls the
  // active address from window.kleverWeb and the cached SDK state.
  const sdkInitRef = useRef(false);
  useEffect(() => {
    if (authUser?.authMethod !== 'wallet' || !authUser.walletAddress) return;
    const expectedAddress = authUser.walletAddress.toLowerCase();

    const ensureSdkInitialised = async () => {
      if (sdkInitRef.current) return;
      try {
        const expectedNetwork = import.meta.env.VITE_KLEVER_NETWORK || 'testnet';
        const providerConfig =
          expectedNetwork === 'testnet'
            ? { api: 'https://api.testnet.klever.org', node: 'https://node.testnet.klever.org' }
            : { api: 'https://api.mainnet.klever.org', node: 'https://node.mainnet.klever.org' };
        web.setProvider(providerConfig);
        await web.initialize();
        sdkInitRef.current = true;
      } catch (err) {
        console.debug('[KleverWallet] SDK init for identity check failed', err);
      }
    };

    const readCurrentAddress = (): string | null => {
      // The extension keeps the active account on window.kleverWeb.address
      // (synchronous read, no IPC, updates when user switches account).
      // @ts-ignore — Klever extension/mobile browser injects window.kleverWeb
      const fromInjected: string | undefined = window?.kleverWeb?.address;
      if (fromInjected) return fromInjected;

      // Fallback to the SDK's last-known address.
      try {
        return web.getWalletAddress() || null;
      } catch {
        return null;
      }
    };

    const verifyWalletMatch = () => {
      const current = readCurrentAddress();
      if (!current) return; // wallet not exposing an address — skip
      if (current.toLowerCase() === expectedAddress) return;
      console.warn(
        `[KleverWallet] Active wallet (${current}) does not match signed-in account (${authUser.walletAddress}). Forcing re-login.`
      );
      toast.error('Wallet account changed. Please sign in again.');
      dispatch(logout());
    };

    // One-time SDK setup so window.kleverWeb is populated even after a
    // hard reload where connectWallet hasn't run yet.
    ensureSdkInitialised();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') verifyWalletMatch();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    const interval = setInterval(verifyWalletMatch, 30_000);
    verifyWalletMatch();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, [authUser?.authMethod, authUser?.walletAddress, dispatch]);

  // Check if Klever Web Extension or Mobile Browser is available
  useEffect(() => {
    const checkExtension = () => {
      // Check for Klever Mobile Browser (has window.kleverWeb injected)
      // @ts-ignore - Klever extension/mobile browser injects window.kleverWeb
      const hasKleverWeb = typeof window !== 'undefined' && window.kleverWeb;

      // Check for Klever SDK web provider (after initialization)
      const hasWebProvider = web && typeof web.getProvider === 'function';

      if (hasKleverWeb || hasWebProvider) {
        setIsExtensionInstalled(true);
        // console.log('[KleverWallet] Klever wallet detected');

        if (hasKleverWeb) {
          // @ts-ignore
          // console.debug('[KleverWallet] window.kleverWeb available:');
        }
        if (hasWebProvider) {
          // console.debug('[KleverWallet] Web SDK provider available');
        }
      } else {
        setIsExtensionInstalled(false);
        console.log('[KleverWallet] No wallet found. Please use Klever Mobile Browser or install Klever Extension.');
      }
    };

    // Check immediately
    checkExtension();

    // Check again after a delay (wallet might load after page)
    const timer = setTimeout(checkExtension, 1000);
    const timer2 = setTimeout(checkExtension, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, []);

  // Connect to Klever wallet
  const connectWallet = async (): Promise<string> => {
    try {
      setIsConnecting(true);
      setError(null);

      //console.debug('[KleverWallet] Requesting wallet connection...');

      // Set provider BEFORE initializing (per Klever docs)
      const expectedNetwork = import.meta.env.VITE_KLEVER_NETWORK || 'testnet';
      //console.debug(`[KleverWallet] Setting provider for ${expectedNetwork}`);

      const providerConfig = expectedNetwork === 'testnet' ? {
        api: 'https://api.testnet.klever.org',
        node: 'https://node.testnet.klever.org'
      } : {
        api: 'https://api.mainnet.klever.org',
        node: 'https://node.mainnet.klever.org'
      };

      web.setProvider(providerConfig);
      //console.debug('[KleverWallet] Provider config set');

      // Initialize Klever Web SDK
      await web.initialize();
      //console.debug('[KleverWallet] SDK initialized');

      // Get the wallet address using the official SDK method
      const walletAddress = web.getWalletAddress();
     // console.debug('[KleverWallet] Wallet address:', walletAddress);

      if (!walletAddress) {
        throw new Error('No wallet address found. Please make sure your Klever wallet is unlocked.');
      }

      setAddress(walletAddress);
      setIsConnected(true);

      console.log('[KleverWallet] Successfully connected:', walletAddress);

      // Fetch balance
      await fetchBalance(walletAddress);

      return walletAddress; // Return the address
    } catch (err: any) {
      console.error('[KleverWallet] Connection error:', err);
      setError(err.message || 'Failed to connect wallet');
      setIsConnected(false);
      setAddress(null);
      throw err; // Re-throw to allow caller to handle
    } finally {
      setIsConnecting(false);
    }
  };

  // Sign an arbitrary message — used for wallet auth challenge
  const signMessage = async (message: string): Promise<string> => {
    // @ts-ignore - Klever extension/mobile browser injects window.kleverWeb
    const kleverWeb = window.kleverWeb;

    if (!kleverWeb?.signMessage) {
      throw new Error('Your Klever wallet does not support message signing. Please update to the latest version.');
    }

    let sig: any;
    try {
      sig = await kleverWeb.signMessage(message);
    } catch (e) {
      console.error('[WalletAuth] kleverWeb.signMessage threw:', e);
      throw e;
    }

    if (!sig) throw new Error('Wallet returned no signature');
    if (typeof sig === 'string') return sig;
    if ((sig as any).signature) {
      const inner = (sig as any).signature;
      if (typeof inner === 'string') return inner;
      if (inner instanceof Uint8Array) {
        return Array.from(inner).map((b: number) => b.toString(16).padStart(2, '0')).join('');
      }
    }
    if (sig instanceof Uint8Array) {
      return Array.from(sig).map((b: number) => b.toString(16).padStart(2, '0')).join('');
    }
    throw new Error('Unrecognised signature format from wallet');
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setIsConnected(false);
    setAddress(null);
    setBalance(null);
    setError(null);
    console.log('[KleverWallet] Disconnected');
  };

  // Fetch KLV balance from Klever API
  const fetchBalance = async (walletAddress: string) => {
    try {
      // Use the configured network to fetch balance
      const network = import.meta.env.VITE_KLEVER_NETWORK || 'testnet';
      const apiUrl = network === 'testnet'
        ? 'https://api.testnet.klever.org'
        : 'https://api.mainnet.klever.org';

      console.log('[KleverWallet] Fetching balance from', network, 'API...');
      const response = await fetch(`${apiUrl}/v1.0/address/${walletAddress}`);
      const data = await response.json();

      const balanceData = data.data?.account?.balance;

      if (balanceData) {
        const klvBalance = balanceData?.KLV || 0;
        setBalance(klvBalance / 1000000); // Convert from micro-KLV
        //console.debug('[KleverWallet] Balance:', klvBalance / 1000000, 'KLV');
      }
    } catch (err) {
      console.error('[KleverWallet] Failed to fetch balance:', err);
      // Don't throw - balance is optional
      setBalance(null);
    }
  };

  // Buy ITO tokens
  const buyITOTokens = async (amountKLYX: number, pricePerToken: number, receiverAddress: string): Promise<string> => {
    try {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      setError(null);

      // Calculate total cost in KLV
      const totalCostKLV = amountKLYX * pricePerToken;

      console.log('[KleverWallet] Initiating ITO purchase:', {
        amountKLYX,
        pricePerToken,
        totalCostKLV,
        receiverAddress,
      });

      // Get KDA ID from environment
      const KLYX_KDA_ID = import.meta.env.VITE_KLYX_KDA_ID || '';

      if (!receiverAddress || !KLYX_KDA_ID) {
        throw new Error('ITO configuration missing. Please contact support.');
      }

      // Build transfer transaction payload
      // This triggers ITO purchase on Klever blockchain
      // @ts-ignore
      const tx = await window.kleverWeb.buildTransaction({
        type: 'Transfer', // or 'Buy' if ITO has specific contract
        payload: {
          receiver: receiverAddress,
          amount: Math.floor(totalCostKLV * 1000000), // Convert to micro-KLV
          kda: 'KLV', // Paying with KLV
        },
      });

      // Sign and broadcast transaction
      // @ts-ignore
      const result = await window.kleverWeb.signAndSend(tx);

      if (!result || !result.hash) {
        throw new Error('Transaction failed or was rejected');
      }

      const txHash = result.hash;
      console.log('[KleverWallet] Transaction successful:', txHash);

      // Refresh balance after purchase
      await fetchBalance(address);

      return txHash;
    } catch (err: any) {
      console.error('[KleverWallet] Purchase error:', err);
      const errorMessage = err.message || 'Transaction failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Freeze tokens for FPR staking
  const freezeTokens = async (amount: number, kdaId: string): Promise<string> => {
    try {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      setError(null);
      console.log('[KleverWallet] Freezing tokens:', { amount, kdaId });

      // @ts-ignore
      const tx = await window.kleverWeb.buildTransaction({
        type: 'Freeze',
        payload: {
          amount: Math.floor(amount * 100000000), // Convert to satoshi
          kda: kdaId,
        },
      });

      // @ts-ignore
      const result = await window.kleverWeb.signAndSend(tx);

      if (!result || !result.hash) {
        throw new Error('Transaction failed or was rejected');
      }

      console.log('[KleverWallet] Freeze successful:', result.hash);
      await fetchBalance(address);
      return result.hash;
    } catch (err: any) {
      console.error('[KleverWallet] Freeze error:', err);
      const errorMessage = err.message || 'Freeze transaction failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Unfreeze tokens
  const unfreezeTokens = async (bucketId: string, kdaId: string): Promise<string> => {
    try {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      setError(null);
      console.log('[KleverWallet] Unfreezing tokens:', { bucketId, kdaId });

      // @ts-ignore
      const tx = await window.kleverWeb.buildTransaction({
        type: 'Unfreeze',
        payload: {
          bucketID: bucketId,
          kda: kdaId,
        },
      });

      // @ts-ignore
      const result = await window.kleverWeb.signAndSend(tx);

      if (!result || !result.hash) {
        throw new Error('Transaction failed or was rejected');
      }

      console.log('[KleverWallet] Unfreeze successful:', result.hash);
      await fetchBalance(address);
      return result.hash;
    } catch (err: any) {
      console.error('[KleverWallet] Unfreeze error:', err);
      const errorMessage = err.message || 'Unfreeze transaction failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Claim staking rewards
  const claimStakingRewards = async (claimType: number, kdaId: string): Promise<string> => {
    try {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      setError(null);
      console.log('[KleverWallet] Claiming rewards:', { claimType, kdaId });

      // @ts-ignore
      const tx = await window.kleverWeb.buildTransaction({
        type: 'Claim',
        payload: {
          claimType, // 0 for StakingClaim
          id: kdaId,
        },
      });

      // @ts-ignore
      const result = await window.kleverWeb.signAndSend(tx);

      if (!result || !result.hash) {
        throw new Error('Transaction failed or was rejected');
      }

      console.log('[KleverWallet] Claim successful:', result.hash);
      await fetchBalance(address);
      return result.hash;
    } catch (err: any) {
      console.error('[KleverWallet] Claim error:', err);
      const errorMessage = err.message || 'Claim transaction failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Deposit to fee pool (regular transfer to pool address)
  const depositToFeePool = async (amount: number, poolAddress: string, kdaId: string): Promise<string> => {
    try {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      setError(null);
      console.log('[KleverWallet] Depositing to fee pool:', { amount, poolAddress, kdaId });

      // @ts-ignore
      const tx = await window.kleverWeb.buildTransaction({
        type: 'Transfer',
        payload: {
          receiver: poolAddress,
          amount: Math.floor(amount * 100000000), // Convert to satoshi
          kda: kdaId,
        },
      });

      // @ts-ignore
      const result = await window.kleverWeb.signAndSend(tx);

      if (!result || !result.hash) {
        throw new Error('Transaction failed or was rejected');
      }

      console.log('[KleverWallet] Deposit successful:', result.hash);
      await fetchBalance(address);
      return result.hash;
    } catch (err: any) {
      console.error('[KleverWallet] Deposit error:', err);
      const errorMessage = err.message || 'Deposit transaction failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const value: KleverWalletContextType = {
    isConnected,
    isConnecting,
    address,
    balance,
    connectWallet,
    disconnectWallet,
    signMessage,
    buyITOTokens,
    freezeTokens,
    unfreezeTokens,
    claimStakingRewards,
    depositToFeePool,
    error,
    isExtensionInstalled,
  };

  return (
    <KleverWalletContext.Provider value={value}>
      {children}
    </KleverWalletContext.Provider>
  );
};

// Custom hook to use Klever wallet
export const useKleverWallet = (): KleverWalletContextType => {
  const context = useContext(KleverWalletContext);
  if (!context) {
    throw new Error('useKleverWallet must be used within KleverWalletProvider');
  }
  return context;
};

export default KleverWalletContext;
